import React from "react";
import { Modal } from "../../Components/Modal";
import TaxonomyMultiSelect from "../../Components/TaxonomyMultiSelect";
import type { StorageObject } from "../../API";
import { formatRelativeTime, formatExactDate } from "../../Helper/dates";
import { formatFileSize } from "./utils";

interface FilePreviewModalProps {
	previewOpen: boolean;
	previewObject: StorageObject | null;
	previewAssetUrl: string | null;
	previewOriginalUrl: string | null;
	previewCompressedUrl: string | null;
	previewTaxonomyIds: string[];
	previewTaxonomySaving: boolean;
	previewTaxonomyMessage: string | null;
	previewTaxonomyEditing: boolean;
	recompressing: boolean;
	taxonomies: any[];
	taxonomyMap: Map<string, string>;
	onClose: () => void;
	onTaxonomyEditToggle: () => void;
	onTaxonomyIdsChange: (ids: string[]) => void;
	onSaveTaxonomies: () => void;
	onRecompress: () => void;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
	previewOpen,
	previewObject,
	previewAssetUrl,
	previewOriginalUrl,
	previewCompressedUrl,
	previewTaxonomyIds,
	previewTaxonomySaving,
	previewTaxonomyMessage,
	previewTaxonomyEditing,
	recompressing,
	taxonomies,
	taxonomyMap,
	onClose,
	onTaxonomyEditToggle,
	onTaxonomyIdsChange,
	onSaveTaxonomies,
	onRecompress
}) => (
	<Modal show={previewOpen} onHide={onClose} size="lg" centered>
		<Modal.Header closeButton>
			<div>
				<Modal.Title>{previewObject?.title ?? previewObject?.fileName ?? "Preview"}</Modal.Title>
				<p className="text-sm text-slate-500">{previewObject?.folder ?? "\u2014"}</p>
			</div>
		</Modal.Header>
		<Modal.Body className="bg-slate-50">
			{previewObject ? (
				<div className="space-y-4">
					<div className="rounded-2xl border border-slate-200 bg-white p-4">
						{previewAssetUrl ? (
							previewObject.contentType?.startsWith("image/") ? (
								<img src={previewAssetUrl} alt={previewObject.title ?? previewObject.fileName ?? "Preview"} className="mx-auto max-h-[60vh] rounded-xl object-contain" />
							) : previewObject.contentType === "application/pdf" ? (
								<iframe
									src={`${previewAssetUrl}#view=FitH`}
									title="PDF preview"
									className="h-[60vh] w-full rounded-xl bg-white"
									loading="lazy"
								/>
							) : (
								<div className="text-center text-sm text-slate-500">
									<p>Preview not available for this file type.</p>
									<p className="mt-1 text-xs text-slate-400">{previewObject.contentType ?? "Unknown type"}</p>
								</div>
							)
						) : (
							<div className="text-center text-sm text-slate-500">
								<p>No public URL available for this asset.</p>
							</div>
						)}
					</div>
					<div className="grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
						<div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">File name</p>
							<p className="mt-1 break-all font-semibold text-slate-900">{previewObject.fileName ?? "\u2014"}</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Size</p>
							<p className="mt-1 font-semibold text-slate-900">{formatFileSize(previewObject.sizeBytes)}</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Updated</p>
							<p className="mt-1 font-semibold text-slate-900">
								{formatRelativeTime(previewObject.updatedAt)}
								<span className="ml-1 text-xs text-slate-400">({formatExactDate(previewObject.updatedAt)})</span>
							</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
							<div className="flex items-center justify-between">
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Taxonomies</p>
								<button
									type="button"
									onClick={() => {
										if (!previewTaxonomyEditing) {
											onTaxonomyEditToggle();
											return;
										}
										onSaveTaxonomies();
									}}
									disabled={previewTaxonomySaving || !previewObject.id}
									className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
								>
									{previewTaxonomySaving ? "Saving\u2026" : previewTaxonomyEditing ? "Save" : "Edit"}
								</button>
							</div>
							<div className="mt-2">
								{previewTaxonomyEditing ? (
									<TaxonomyMultiSelect
										options={taxonomies}
										value={previewTaxonomyIds}
										onChange={onTaxonomyIdsChange}
										placeholder="Assign taxonomies"
										helper="Filter and select taxonomies for this asset."
									/>
								) : (
									<div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
										{previewObject.taxonomyIds?.length
											? previewObject.taxonomyIds.map((id) => taxonomyMap.get(id) ?? id).join(", ")
											: "No taxonomies assigned."}
									</div>
								)}
							</div>
							{previewTaxonomyMessage && (
								<p className="mt-2 text-xs text-slate-500">{previewTaxonomyMessage}</p>
							)}
						</div>
					</div>
				</div>
			) : (
				<p className="text-sm text-slate-500">No asset selected.</p>
			)}
		</Modal.Body>
		<Modal.Footer>
			{previewObject?.contentType?.startsWith("image/") && (
				<button
					type="button"
					onClick={onRecompress}
					disabled={recompressing || !previewObject.id}
					className="rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{recompressing ? "Compressing\u2026" : "Recompress image"}
				</button>
			)}
			{previewObject && previewCompressedUrl && (
				<a
					href={previewCompressedUrl}
					target="_blank"
					rel="noreferrer"
					className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Open compressed
				</a>
			)}
			{previewObject && previewOriginalUrl && previewOriginalUrl !== previewCompressedUrl && (
				<a
					href={previewOriginalUrl}
					target="_blank"
					rel="noreferrer"
					className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Open original
				</a>
			)}
			<button
				type="button"
				onClick={onClose}
				className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
			>
				Close
			</button>
		</Modal.Footer>
	</Modal>
);
